import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {
    AriaMenuItem,
    AriaSubMenuContentHelpers,
    ButtonVariant,
} from '@pexip/components';
import {NotificationTooltip, UserMenuButton} from '@pexip/components';
import {TOOLTIP_Z_INDEX_BELOW_OVERLAYS} from '@pexip/media-components';

import {TestId} from '../../test/testIds';
import {useBranding} from '../branding/Context';
import {INITIAL_USER_MENU_STEP} from '../constants';
import type {AutoHideUIContextValue} from '../contexts/autoHideUI.context';
import {useUserMenuContent} from '../hooks/useUserMenuContent';
import {userMenuStepSignal} from '../signals/Menu.signals';
import {skippedStepByStepSignal} from '../signals/StepByStep.signals';
import type {UserMenuStep} from '../types';
import {ConfirmationModalStates} from '../types';
import {UserMenu as UserMenuView} from '../views/UserMenu/UserMenu.view';

import {About} from './About.viewModel';
import {AccessibilitySettings} from './AccessibilitySettings.viewModel';
import {AdvancedSettings} from './AdvancedSettings.viewModel';
import {JoiningSettings} from './JoiningSettings.viewModel';
import {Languages} from './Languages.viewModel';
import {Layouts} from './Layouts.viewModel';
import {NerdStats} from './NerdStats.viewModel';
import {Quality} from './Quality.viewModel';
import {VideoAndSound} from './VideoAndSound.viewModel';

export const UserMenu: React.FC<{
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    autoHideProps?: AutoHideUIContextValue['autoHideProps'];
    toggleAutoHideUI?: AutoHideUIContextValue['toggleAutoHideUI'];
    variant?: ButtonVariant;
    hasMedia?: boolean;
    isInMeeting?: boolean;
    leave?: () => void;
}> = ({
    isInMeeting = false,
    showLeave = false,
    showMediaStatistics,
    showQuality,
    hasMedia = false,
    variant,
    autoHideProps,
    toggleAutoHideUI,
    leave,
}) => {
    const {t} = useTranslation();
    const overlay = useBranding('overlay');

    const [isOpen, setIsOpen] = useState(false);
    const [isStatsOpen, setIsStatsOpen] = useState(false);
    const [confirmationState, setConfirmationState] = useState(
        ConfirmationModalStates.None,
    );
    const [
        isTestVideoAndSoundTooltipVisible,
        setIsTestVideoAndSoundTooltipVisible,
    ] = useState(false);

    const {
        downloadLogWithName,
        canChangeLayout,
        canChangePersonalLayout,
        isDirectMedia,
        isFeedbackEnabled,
        openFeedbackForm,
        pluginActions,
    } = useUserMenuContent({isInMeeting});

    const emitStep = useCallback((step: UserMenuStep) => {
        userMenuStepSignal.emit(step);
    }, []);

    const handleOpenChange = useCallback(
        (isMenuOpen: boolean) => {
            setIsOpen(isMenuOpen);
            toggleAutoHideUI?.('openedUserMenu', isMenuOpen);
            if (!isMenuOpen) {
                userMenuStepSignal.emit(INITIAL_USER_MENU_STEP);
            }
        },
        [toggleAutoHideUI],
    );

    const handleGoBack = useCallback(() => {
        userMenuStepSignal.emit(INITIAL_USER_MENU_STEP);
    }, []);

    const openStats = useCallback(() => {
        setIsStatsOpen(true);
    }, []);

    useEffect(
        () =>
            skippedStepByStepSignal.add(() => {
                setIsTestVideoAndSoundTooltipVisible(true);
            }),
        [],
    );

    const pluginMenuActions: AriaMenuItem[] = pluginActions.map(
        (action, index) => ({
            key: `plugin-action-${index}`,
            label: action.label,
            testId: action.testId,
            onItemClick: action.clickAction as (() => void) | undefined,
            closeMenuOnItemClick: false,
        }),
    );

    const subMenuContent = {
        videoAndSound: ({close}: AriaSubMenuContentHelpers) => (
            <VideoAndSound close={close} />
        ),
        joining: <JoiningSettings />,
        quality: <Quality />,
        language: <Languages />,
        layouts: ({close}: AriaSubMenuContentHelpers) => (
            <Layouts
                close={close}
                isConfirmationOpen={
                    confirmationState === ConfirmationModalStates.Layout
                }
                openConfirmation={() =>
                    setConfirmationState(ConfirmationModalStates.Layout)
                }
                closeConfirmation={() =>
                    setConfirmationState(ConfirmationModalStates.None)
                }
            />
        ),
        accessibility: <AccessibilitySettings />,
        advanced: <AdvancedSettings />,
        about: <About />,
    };

    const triggerButtonProps: {
        'data-testid': string;
        'aria-label': string;
    } = {
        'data-testid': TestId.UserMenuButton,
        'aria-label': t('settings.menu-button-label', 'Toggle settings menu'),
    };

    const button = (
        <UserMenuButton
            {...triggerButtonProps}
            isContentVisible={isOpen}
            colorScheme={overlay ?? 'dark'}
            variant={variant}
            modifier="square"
        />
    );

    return (
        <>
            <NotificationTooltip
                shouldCloseOnOutsideClick
                zIndex={TOOLTIP_Z_INDEX_BELOW_OVERLAYS}
                position="bottomLeft"
                header={t(
                    'test-video-and-sound-menu-tooltip.title',
                    'Test video and sound',
                )}
                text={t(
                    'test-video-and-sound-menu-tooltip.description',
                    'You can test your video and sound anytime from the menu',
                )}
                controlledIsVisible={isTestVideoAndSoundTooltipVisible}
                controlledOnChange={setIsTestVideoAndSoundTooltipVisible}
            >
                <div {...autoHideProps}>
                    <UserMenuView
                        hasMedia={hasMedia}
                        isDirectMedia={isDirectMedia}
                        isFeedbackEnabled={isFeedbackEnabled}
                        isInMeeting={isInMeeting}
                        canChangeLayout={canChangeLayout}
                        canChangePersonalLayout={canChangePersonalLayout}
                        showLeave={showLeave}
                        showQuality={showQuality}
                        showMediaStatistics={showMediaStatistics}
                        pluginActions={pluginMenuActions}
                        downloadLogWithName={downloadLogWithName}
                        openFeedbackForm={openFeedbackForm}
                        onLeave={leave}
                        emitStep={emitStep}
                        openStats={openStats}
                        subMenuContent={subMenuContent}
                        button={button}
                        onOpenChange={handleOpenChange}
                        onGoBack={handleGoBack}
                        colorScheme="light"
                    />
                </div>
            </NotificationTooltip>
            {isStatsOpen && <NerdStats onClose={() => setIsStatsOpen(false)} />}
        </>
    );
};
